export interface OnboardingStep {
    id: string;
    route: string;
    skippable: boolean;
}

export interface OnboardingState {
    completedSteps: string[];
    completedAt: any | null; // Firestore Timestamp or null
    /**
     * Which steps this organisation actually has to finish. Written at creation
     * time because it varies by organisation type, and read by both the Angular
     * onboarding guard and the beforeSignIn blocking function — the two used to
     * keep separate hardcoded copies of this list, which had to agree.
     *
     * Absent on organisations created before standalone support; treat that as
     * the full Planning Center list.
     */
    requiredSteps?: string[];
    /**
     * How each step was actually finished, keyed by step id.
     *
     * completedSteps cannot answer this: it records a skipped step exactly like
     * a completed one, which is what made every organisation look fully set up
     * in the platform console regardless of what they had actually done.
     *
     * 'not_applicable' is for steps nobody could have completed rather than
     * chose not to — the business-details and messaging-limit steps when no
     * WhatsApp number is connected for them to apply to.
     *
     * Absent on organisations that onboarded before this field existed; the
     * console derives their status from the underlying data instead.
     */
    stepOutcomes?: Record<string, { outcome: 'completed' | 'skipped' | 'not_applicable'; at?: any }>;
}

/**
 * Every onboarding step and where it lives.
 * To add a new step, append it here — existing orgs missing it
 * will be routed to it on their next visit.
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
    { id: 'webhook-setup', route: '/webhook-setup', skippable: false },
    { id: 'location-setup', route: '/location-setup', skippable: false },
    { id: 'whatsapp-setup', route: '/whatsapp-setup', skippable: true },
    { id: 'business-details-setup', route: '/business-details-setup', skippable: true },
    { id: 'messaging-setup', route: '/messaging-setup', skippable: true },
];

/**
 * Steps that only make sense for an organisation connected to Planning Center.
 * `webhook-setup` subscribes to PCO webhooks, so a standalone organisation has
 * nothing to configure there and must not be routed to it.
 */
export const PCO_ONLY_STEPS = ['webhook-setup'];

/**
 * Steps only a standalone organisation needs.
 *
 * Planning Center supplies an organisation's country and timezone at creation.
 * Nothing supplies them otherwise, and there was no way to enter them —
 * without a country, per-message cost estimates cannot resolve a rate and come
 * back blank.
 */
export const STANDALONE_ONLY_STEPS = ['location-setup'];

/**
 * Steps that configure an already-connected WhatsApp number.
 *
 * An organisation that skipped WhatsApp setup has no number for a business
 * profile to attach to and no messaging limits to set, so these steps can do
 * nothing. Both are reachable later from Organization Settings once a number
 * is connected.
 */
export const WHATSAPP_DEPENDENT_STEPS = ['business-details-setup', 'messaging-setup'];

/**
 * The steps an organisation of the given type must complete.
 * Mirrored server-side in functions/src/services/onboarding.ts.
 */
export function requiredStepsFor(orgType: 'pco' | 'standalone'): string[] {
    return ONBOARDING_STEPS
        .filter(step => (orgType === 'pco' ?
            !STANDALONE_ONLY_STEPS.includes(step.id) :
            !PCO_ONLY_STEPS.includes(step.id)))
        .map(step => step.id);
}

/**
 * Resolve the steps that apply to an organisation, preferring what was stored
 * on the document and falling back to the full list for older organisations.
 */
export function resolveRequiredSteps(state: OnboardingState | null | undefined): OnboardingStep[] {
    const required = state?.requiredSteps;
    if (!required || required.length === 0) {
        // No stored list means the organisation predates the field, so it is a
        // Planning Center one. Falling back to every step would add
        // location-setup and mark long-finished orgs as incomplete.
        const pcoSteps = requiredStepsFor('pco');
        return ONBOARDING_STEPS.filter(step => pcoSteps.includes(step.id));
    }
    return ONBOARDING_STEPS.filter(step => required.includes(step.id));
}

/**
 * The steps an organisation can actually act on right now.
 *
 * Narrower than resolveRequiredSteps: it also drops the steps that configure a
 * WhatsApp number when no number is connected. Walking someone through a form
 * that cannot save is worse than sending them to the dashboard and letting
 * them do it from Settings after they connect.
 */
export function applicableSteps(
    state: OnboardingState | null | undefined,
    context: { whatsappConnected: boolean; needsLocation?: boolean }
): OnboardingStep[] {
    let steps = resolveRequiredSteps(state);

    // Standalone organisations created before location-setup existed have it
    // missing from their stored requiredSteps, but still have no country. Add
    // it rather than leaving them permanently without one.
    if (context.needsLocation && !steps.some(s => s.id === 'location-setup')) {
        const location = ONBOARDING_STEPS.find(s => s.id === 'location-setup');
        if (location) steps = [location, ...steps];
    }

    return steps.filter(
        step => context.whatsappConnected || !WHATSAPP_DEPENDENT_STEPS.includes(step.id)
    );
}
